import { useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { fileToReceiptDataUrl, dataUrlBytes } from '../../utils/receiptImage'

/**
 * Picks a receipt - camera or file - and hands back { dataUrl, name, bytes }.
 *
 * It owns nothing beyond the picking. The caller decides when the bytes are
 * sent, which is what lets the same control sit on a new line (posted along
 * with the create) and on an edit (posted against the saved row afterwards).
 *
 * `hasExisting` switches the wording from taking a first photo to replacing the
 * one already on the row, because on a correction that is usually what is
 * happening - the original was blurred, or the wrong receipt got attached.
 */
export default function ReceiptPicker({
    value,
    onChange,
    hasExisting = false,
    onViewExisting,
    viewingExisting = false,
    compact = false,
}) {
    const cameraRef = useRef(null)
    const fileRef = useRef(null)
    const [processing, setProcessing] = useState(false)

    async function handleFile(file) {
        if (!file) return
        setProcessing(true)
        try {
            const dataUrl = await fileToReceiptDataUrl(file)
            onChange({ dataUrl, name: file.name || 'receipt', bytes: dataUrlBytes(dataUrl) })
        } catch (error) {
            toast.error(error.message || 'That receipt could not be processed')
        }
        setProcessing(false)
    }

    // Clears the pick, not the stored receipt - so on a row that already has one,
    // this is "changed my mind", and the saved image is left alone.
    function clear() {
        onChange(null)
        if (cameraRef.current) cameraRef.current.value = ''
        if (fileRef.current) fileRef.current.value = ''
    }

    const replacing = hasExisting || !!value
    const isPdf = value?.dataUrl?.startsWith('data:application/pdf')

    // On the desktop table the control lives inside a cell, where a pair of full
    // buttons would blow the row height out; on a phone they are the touch target.
    const pickClass = compact
        ? 'text-rdi-blue text-xs hover:underline cursor-pointer whitespace-nowrap'
        : 'btn btn-secondary cursor-pointer'
    const linkClass = compact
        ? 'text-gray-500 text-xs hover:underline whitespace-nowrap'
        : 'text-sm text-gray-500 hover:underline'
    const thumbClass = compact ? 'w-10 h-10 shrink-0' : 'w-16 h-16 shrink-0'

    return (
        <div>
            <div className={`flex flex-wrap items-center ${compact ? 'gap-x-3 gap-y-1' : 'gap-2'}`}>
                <label className={pickClass}>
                    {replacing ? '📷 Retake' : '📷 Take photo'}
                    <input
                        ref={cameraRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        style={{ display: 'none' }}
                        onChange={e => handleFile(e.target.files?.[0])}
                    />
                </label>

                <label className={pickClass}>
                    {replacing ? '🖼️ Replace' : '🖼️ Choose file'}
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*,application/pdf"
                        style={{ display: 'none' }}
                        onChange={e => handleFile(e.target.files?.[0])}
                    />
                </label>

                {hasExisting && onViewExisting && (
                    <button
                        type="button"
                        className={linkClass}
                        onClick={onViewExisting}
                        disabled={viewingExisting}
                    >
                        {viewingExisting ? 'Opening…' : '📎 View current'}
                    </button>
                )}

                {processing && <span className="text-xs text-gray-500">Processing…</span>}
            </div>

            {value ? (
                <div className={compact
                    ? 'mt-2 flex items-start gap-2'
                    : 'mt-3 flex items-start gap-3 p-3 bg-gray-50 rounded-lg'}>
                    {isPdf ? (
                        <div className={`${thumbClass} flex items-center justify-center bg-white border rounded text-xl`}>
                            📄
                        </div>
                    ) : (
                        <img
                            src={value.dataUrl}
                            alt="Receipt preview"
                            className={`${thumbClass} object-cover rounded border`}
                        />
                    )}
                    <div className="min-w-0 flex-1 text-xs">
                        <p className="font-medium text-gray-700 break-all">{value.name}</p>
                        <p className="text-gray-500">
                            {Math.round(value.bytes / 1024)} KB
                            {hasExisting && ' — replaces the current receipt on save'}
                        </p>
                        <button type="button" className="text-rdi-red hover:underline mt-0.5" onClick={clear}>
                            {hasExisting ? 'Keep the current one' : 'Remove'}
                        </button>
                    </div>
                </div>
            ) : (
                !compact && (
                    <p className="text-xs text-gray-500 mt-2">
                        {hasExisting
                            ? 'A receipt is already attached. A new photo replaces it when you save.'
                            : 'Photos are resized to about 1600px before they are sent, so they stay small.'}
                    </p>
                )
            )}
        </div>
    )
}
